import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { toast } from 'sonner';
import { bonusAccountService } from '@/entities/BonusAccount/api/bonusAccountService';
import {
    bonusOperationFormValuesToAccrueRequest,
    bonusOperationFormValuesToSpendRequest,
} from '@/entities/BonusAccount/mappers';

import {
    BONUS_OPERATION_FORM_DEFAULTS,
    type BonusOperationFormValues,
} from '@/entities/BonusAccount/model/schema';
import { BonusOperationKind } from '@/entities/BonusAccount/model/types';
import { getErrorMessage } from '@/shared/lib/errors';
import { isBadRequestError } from '@/core/api/badRequestError';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError } from '@/shared/ui/RequestState';
import { useBonusOperationForm } from './useBonusOperationForm';

interface BonusOperationModalProps {
    visible: boolean;
    kind: BonusOperationKind;
    clientId: string;
    onHide: () => void;
    onSuccess: () => Promise<void> | void;
}

const {
    NumberField: BonusOperationNumberField,
    TextareaField: BonusOperationTextareaField,
} = createRHFFields<BonusOperationFormValues>();

/** Отображает операцию начисления или списания бонусов */
export const BonusOperationModal = (
    {
        visible,
        kind,
        clientId,
        onHide,
        onSuccess,
    }: BonusOperationModalProps
) => {
    const form = useBonusOperationForm(visible);
    const isAccrual = kind === BonusOperationKind.Accrue;
    const pending = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const busy = submitting || form.formState.isSubmitting;

    const attempt = useRef<{
        kind: BonusOperationKind;
        clientId: string;
        amount: number | null;
        description: string;
        operationId: string;
    } | null>(null);

    /** Закрывает бонусную операцию с подтверждением изменений */
    const handleHide = () => {
        if (pending.current) return;

        confirmUnsavedChanges(form.formState.isDirty, () => {
            form.reset(BONUS_OPERATION_FORM_DEFAULTS);
            onHide();
        });
    };

    useEffect(() => {
        if (!visible) return;

        attempt.current = null;
        form.reset(BONUS_OPERATION_FORM_DEFAULTS);
    }, [visible, form, clientId, kind]);

    /** Выполняет начисление или списание бонусов */
    const submit = useCallback(async (values: BonusOperationFormValues) => {
        if (pending.current) return;

        pending.current = true;
        setSubmitting(true);
        form.clearErrors('root.server');

        const sameAttempt = attempt.current?.clientId === clientId && attempt.current.kind === kind
            && attempt.current.amount === values.amount && attempt.current.description === values.description.trim();

        if (!sameAttempt || !attempt.current) {
            attempt.current = {
                kind,
                clientId,
                amount: values.amount,
                description: values.description.trim(),
                operationId: crypto.randomUUID(),
            };
        }

        try {
            try {
                if (isAccrual) {
                    await bonusAccountService.accrue(
                        bonusOperationFormValuesToAccrueRequest(values, clientId, attempt.current.operationId),
                    );
                } else {
                    await bonusAccountService.spend(
                        bonusOperationFormValuesToSpendRequest(values, clientId, attempt.current.operationId),
                    );
                }
            } catch (error: unknown) {
                if (isBadRequestError(error)) return;

                form.setError('root.server', {
                    type: 'server',
                    message: getErrorMessage(error, 'Не удалось выполнить бонусную операцию'),
                });

                return;
            }

            attempt.current = null;
            form.reset(BONUS_OPERATION_FORM_DEFAULTS);

            toast.success(isAccrual
                ? 'Бонусы начислены'
                : 'Бонусные баллы списаны');

            onHide();

            try {
                await onSuccess();
            } catch {
                toast.error('Операция выполнена. Не удалось обновить данные страницы');
            }
        } finally {
            pending.current = false;
            setSubmitting(false);
        }
    }, [clientId, form, isAccrual, kind, onHide, onSuccess]);

    return (
        <FormProvider {...form}>
            <Modal
                visible={visible}
                onHide={handleHide}
                closable={!busy}
                title={isAccrual
                    ? 'Начислить бонусы'
                    : 'Списать бонусные баллы'
                }
                size="sm"
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Отмена"
                            severity="secondary"
                            outlined
                            onClick={handleHide}
                            disabled={busy}
                        />
                        <Button
                            label={isAccrual
                                ? 'Начислить'
                                : 'Списать'
                            }
                            severity={isAccrual
                                ? 'success'
                                : 'warning'
                            }
                            loading={busy}
                            onClick={form.handleSubmit(submit)}
                        />
                    </div>
                )}
            >
                <form
                    onSubmit={form.handleSubmit(submit)}
                    className="flex flex-column gap-4"
                    noValidate
                >
                    <BonusOperationNumberField
                        name="amount"
                        label="Количество баллов"
                        min={0.01}
                        isRequired
                        disabled={busy}
                    />
                    <BonusOperationTextareaField
                        name="description"
                        label="Комментарий"
                        placeholder="Причина операции"
                        rows={4}
                        disabled={busy}
                    />
                    {form.formState.errors.root?.server?.message && (
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    )}
                </form>
            </Modal>
        </FormProvider>
    );
};
